import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api/client";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import AdminLoading from "./AdminLoading";
import { IoChevronDown, IoChevronUp, IoCheckmarkCircle, IoAlertCircle } from "react-icons/io5";

/* ─────────────────────────────────────────────────────────────────────────── */
/* Global SEO Tab (existing functionality)                                     */
/* ─────────────────────────────────────────────────────────────────────────── */
function GlobalSeoTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    google_site_verification: "",
    bing_site_verification: "",
    twitter_handle: "",
    default_og_image_url: "",
    organization_name: "",
    organization_logo_url: "",
    organization_url: "",
    same_as_text: "",
  });

  const load = () => {
    setLoading(true);
    setError("");
    api("/api/admin/settings/seo")
      .then((data) => {
        setForm({
          google_site_verification: data.google_site_verification ?? "",
          bing_site_verification: data.bing_site_verification ?? "",
          twitter_handle: data.twitter_handle ?? "",
          default_og_image_url: data.default_og_image_url ?? "",
          organization_name: data.organization_name ?? "",
          organization_logo_url: data.organization_logo_url ?? "",
          organization_url: data.organization_url ?? "",
          same_as_text: data.same_as_text ?? "",
        });
      })
      .catch((e) => setError(e.message || "Could not load SEO settings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      await api("/api/admin/settings/seo", {
        method: "PUT",
        body: JSON.stringify({
          google_site_verification: form.google_site_verification.trim(),
          bing_site_verification: form.bing_site_verification.trim(),
          twitter_handle: form.twitter_handle.trim(),
          default_og_image_url: form.default_og_image_url.trim(),
          organization_name: form.organization_name.trim(),
          organization_logo_url: form.organization_logo_url.trim(),
          organization_url: form.organization_url.trim(),
          same_as_text: form.same_as_text,
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">SEO settings saved successfully.</Alert>}
      <Typography variant="subtitle2" color="text.secondary">
        Ownership verification (paste the content values only, not the full meta tag).
      </Typography>
      <TextField size="small" label="Google site verification" value={form.google_site_verification}
        onChange={(e) => setForm((f) => ({ ...f, google_site_verification: e.target.value }))}
        placeholder="Verification string from Search Console meta tag" fullWidth />
      <TextField size="small" label="Bing (msvalidate.01)" value={form.bing_site_verification}
        onChange={(e) => setForm((f) => ({ ...f, bing_site_verification: e.target.value }))} fullWidth />
      <TextField size="small" label="Twitter / X handle" value={form.twitter_handle}
        onChange={(e) => setForm((f) => ({ ...f, twitter_handle: e.target.value.replace(/^@+/, "") }))}
        placeholder="GemFashionStudio" fullWidth />
      <TextField size="small" label="Default OG image URL (https)" value={form.default_og_image_url}
        onChange={(e) => setForm((f) => ({ ...f, default_og_image_url: e.target.value }))}
        placeholder="https://www.example.com/og-default.jpg" fullWidth />
      <Typography variant="subtitle2" sx={{ mt: 2 }}>JSON-LD Organization</Typography>
      <TextField size="small" label="Organization name" value={form.organization_name}
        onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))} fullWidth />
      <TextField size="small" label="Organization logo URL" value={form.organization_logo_url}
        onChange={(e) => setForm((f) => ({ ...f, organization_logo_url: e.target.value }))} fullWidth />
      <TextField size="small" label="Organization canonical URL" value={form.organization_url}
        onChange={(e) => setForm((f) => ({ ...f, organization_url: e.target.value }))}
        placeholder="https://www.example.com" fullWidth />
      <TextField size="small" label="SameAs (Instagram, Facebook…) — one URL per line or comma-separated"
        value={form.same_as_text} onChange={(e) => setForm((f) => ({ ...f, same_as_text: e.target.value }))}
        multiline minRows={3} fullWidth />
      <Button type="submit" variant="contained" disabled={saving} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
        {saving ? "Saving…" : "Save global SEO settings"}
      </Button>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Page-level SEO editor row                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */
function PageSeoRow({ page, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    seo_title: page.seo_title || "",
    seo_description: page.seo_description || "",
    og_image_url: page.og_image_url || "",
    robots: page.robots || "index,follow",
  });

  const hasContent = !!(page.seo_title || page.seo_description);
  const titleLen = form.seo_title.length;
  const descLen = form.seo_description.length;

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const updated = await api(`/api/admin/page-seo/${page.slug}`, {
        method: "PUT",
        body: JSON.stringify({
          seo_title: form.seo_title.trim() || null,
          seo_description: form.seo_description.trim() || null,
          og_image_url: form.og_image_url.trim() || null,
          robots: form.robots || "index,follow",
        }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved(updated);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 1 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", px: 2, py: 1.5, cursor: "pointer", bgcolor: open ? "action.selected" : "transparent", "&:hover": { bgcolor: "action.hover" } }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title={hasContent ? "SEO configured" : "No SEO set — using default fallback"}>
            {hasContent
              ? <IoCheckmarkCircle size={18} color="#2e7d32" />
              : <IoAlertCircle size={18} color="#ed6c02" />
            }
          </Tooltip>
          <Typography variant="body2" fontWeight={600}>{page.label || page.slug}</Typography>
          <Typography variant="caption" color="text.secondary">/{page.slug}</Typography>
          {!hasContent && <Chip label="No SEO" size="small" color="warning" variant="outlined" />}
        </Box>
        <IconButton size="small">{open ? <IoChevronUp /> : <IoChevronDown />}</IconButton>
      </Box>

      <Collapse in={open}>
        <Divider />
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Live snippet preview */}
          {(form.seo_title || form.seo_description) && (
            <Box sx={{ bgcolor: "#fff", border: "1px solid #e0e0e0", borderRadius: 1, p: 2, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Google Search Preview</Typography>
              <Typography sx={{ color: "#1a0dab", fontSize: "1.1rem", lineHeight: 1.3, fontFamily: "Arial, sans-serif" }}>
                {form.seo_title || "(no title set)"}
              </Typography>
              <Typography sx={{ color: "#006621", fontSize: "0.8rem", fontFamily: "Arial, sans-serif" }}>
                gemfashionstudios.in/{page.slug}
              </Typography>
              <Typography sx={{ color: "#545454", fontSize: "0.85rem", fontFamily: "Arial, sans-serif" }}>
                {form.seo_description || "(no description set)"}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              size="small" label="SEO Title" fullWidth
              value={form.seo_title}
              onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))}
              placeholder="Leave blank to use default page title"
              helperText={`${titleLen}/60 chars${titleLen > 60 ? " — too long" : titleLen > 0 && titleLen < 30 ? " — too short" : ""}`}
              error={titleLen > 60}
            />
          </Box>
          <TextField
            size="small" label="Meta Description" fullWidth multiline minRows={2}
            value={form.seo_description}
            onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))}
            placeholder="Leave blank to use site default description"
            helperText={`${descLen}/160 chars${descLen > 160 ? " — too long" : descLen > 0 && descLen < 70 ? " — too short" : ""}`}
            error={descLen > 160}
          />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small" label="OG Image URL" sx={{ flex: 2, minWidth: 240 }}
              value={form.og_image_url}
              onChange={(e) => setForm((f) => ({ ...f, og_image_url: e.target.value }))}
              placeholder="https://… (overrides default OG image for this page)"
            />
            <TextField
              size="small" label="Robots" sx={{ flex: 1, minWidth: 140 }}
              value={form.robots}
              onChange={(e) => setForm((f) => ({ ...f, robots: e.target.value }))}
              placeholder="index,follow"
              helperText="e.g. noindex,nofollow for login"
            />
          </Box>
          {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleSave} disabled={saving} sx={{ textTransform: "none" }}>
              {saving ? <><CircularProgress size={14} sx={{ mr: 1 }} />Saving…</> : "Save"}
            </Button>
            {saved && <Typography variant="caption" color="success.main">Saved ✓</Typography>}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Pages SEO Tab                                                               */
/* ─────────────────────────────────────────────────────────────────────────── */
function PagesSeoTab() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/api/admin/page-seo")
      .then(setPages)
      .catch((e) => setError(e.message || "Could not load page SEO"))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = useCallback((updated) => {
    setPages((prev) => prev.map((p) => p.slug === updated.slug ? { ...p, ...updated } : p));
  }, []);

  if (loading) return <AdminLoading />;

  const configured = pages.filter((p) => p.seo_title || p.seo_description).length;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Edit the meta title and description for each storefront page. Changes take effect immediately — no deploy needed.
        </Typography>
        <Chip label={`${configured}/${pages.length} configured`} size="small" color={configured === pages.length ? "success" : "warning"} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {pages.map((page) => (
        <PageSeoRow key={page.slug} page={page} onSaved={handleSaved} />
      ))}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Root component                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
export default function AdminSeoSettings() {
  const [tab, setTab] = useState(0);

  return (
    <Card>
      <CardHeader
        title="SEO Settings"
        subheader="Global verification tags and per-page meta title / description — similar to RankMath, but for your store."
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Global SEO" sx={{ textTransform: "none" }} />
        <Tab label="Page SEO" sx={{ textTransform: "none" }} />
      </Tabs>
      <CardContent>
        {tab === 0 && <GlobalSeoTab />}
        {tab === 1 && <PagesSeoTab />}
      </CardContent>
    </Card>
  );
}
